import { useState, useEffect } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { uploadMediaToS3 } from '../utils/MediaUpload';
import { CROP_DND_MIME } from './SourceDocViewer';

// Reusable image/audio upload field. Uploads straight to S3 (presigned) and stores
// the resulting URL string via `onChange`. Shows a preview + remove control.
//   kind: "image" | "audio"
// Supports both "choose file" and drag-and-drop: drop an OS file OR a region cropped in the
// right-side SourceDocViewer (image fields only) and it auto-uploads just like choosing a file.
// While a file is dragged over the field the drop zone zooms up and shows the field name.
export default function MediaUploadField({ label, value, onChange, kind = 'image' }) {
    const callApi = useApiCaller();
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const accept = kind === 'audio' ? 'audio/*' : 'image/*';

    // Single upload path shared by choose-file and drop. Rejects a file whose type doesn't
    // match this field's kind (e.g. an image dropped on an audio field).
    const uploadFile = async (file) => {
        if (!file) return;
        const typeOk = kind === 'audio' ? file.type.startsWith('audio/') : file.type.startsWith('image/');
        if (!typeOk) return;
        setBusy(true);
        try {
            const url = await uploadMediaToS3(callApi, file, kind);
            onChange(url);
        } catch (_) {
            // ErrorContext surfaces network errors via useApiCaller; keep the field usable.
        } finally {
            setBusy(false);
        }
    };

    const handleFile = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-selecting the same file later
        uploadFile(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        // 1) OS file drag.
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) { uploadFile(file); return; }
        // 2) A region cropped in the SourceDocViewer (image only) — fetch the blob URL into a File.
        const cropUrl = e.dataTransfer.getData(CROP_DND_MIME) || e.dataTransfer.getData('text/uri-list');
        if (cropUrl && kind === 'image') {
            try {
                const blob = await (await fetch(cropUrl)).blob();
                await uploadFile(new File([blob], 'crop.png', { type: blob.type || 'image/png' }));
            } catch (_) { /* drop source gone */ }
        }
    };

    const allowDrag = (e) => {
        // Accept OS files and crop drags; show the copy cursor + zoom the target.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        if (!dragOver) setDragOver(true);
    };

    // Any drag ending anywhere (dropped on another field, or released with no drop) must clear
    // this field's highlight — otherwise a hovered-but-not-dropped field stays lit, even across
    // questions. Paired with the relatedTarget-aware dragleave below.
    useEffect(() => {
        const clear = () => setDragOver(false);
        document.addEventListener('dragend', clear);
        document.addEventListener('drop', clear);
        return () => {
            document.removeEventListener('dragend', clear);
            document.removeEventListener('drop', clear);
        };
    }, []);

    return (
        <div
            className="mb-2"
            onDragEnter={allowDrag}
            onDragOver={allowDrag}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
            onDrop={handleDrop}
            style={{ position: 'relative' }}
        >
            {label ? <label className="form-label d-block mb-1">{label}</label> : null}
            <div className="d-flex align-items-center gap-2">
                <input
                    type="file"
                    accept={accept}
                    className="form-control form-control-sm"
                    onChange={handleFile}
                    disabled={busy}
                />
                {busy ? <span className="small text-muted">Uploading…</span> : null}
                {value ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onChange('')}
                    >
                        Remove
                    </button>
                ) : null}
            </div>
            {value ? (
                <div className="mt-1">
                    {kind === 'audio' ? (
                        <audio controls src={value} style={{ maxWidth: '100%' }} />
                    ) : (
                        <img src={value} alt="preview" style={{ maxHeight: 90, borderRadius: 4 }} />
                    )}
                </div>
            ) : null}

            {/* Zoomed drop target shown while dragging a file/crop over this field. */}
            {dragOver && (
                <div
                    style={{
                        position: 'absolute', inset: -6, zIndex: 20,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        border: '3px dashed #7c3aed', borderRadius: 10,
                        background: 'rgba(124,58,237,0.12)', backdropFilter: 'blur(1px)',
                        transform: 'scale(1.04)', transition: 'transform 0.08s ease',
                        pointerEvents: 'none', minHeight: 90,
                    }}
                >
                    <div className="fw-bold" style={{ color: '#5b21b6' }}>⬇ Drop to upload</div>
                    <div className="small" style={{ color: '#5b21b6' }}>{label || (kind === 'audio' ? 'Audio' : 'Image')}</div>
                </div>
            )}
        </div>
    );
}
